import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { AnalysisPage } from '@/pages/analysis/AnalysisPage';
import ChatHistoryPage from '@/pages/chat/ChatHistoryPage';

type HubTab = 'analysis' | 'history';

interface PanelErrorBoundaryProps {
  children: React.ReactNode;
  label: string;
}

class PanelErrorBoundary extends React.Component<PanelErrorBoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <h2 className="font-semibold">{this.props.label} is unavailable</h2>
          <p className="mt-1 text-sm">Try reloading the page. You can still use the other AI Hub section.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const STORAGE_KEY = 'aiHub.activeTab';
const isHubTab = (value: string | null): value is HubTab => value === 'analysis' || value === 'history';

const readRememberedTab = (): HubTab | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isHubTab(value) ? value : null;
  } catch {
    return null;
  }
};

const AIHubPage: React.FC = () => {
  const { user } = useAuth();
  const { hasWyscout } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const canAnalyze = hasWyscout && !!user && ['admin', 'manager'].includes(user.role);
  const requested = searchParams.get('tab');
  const permitted = (tab: HubTab) => tab === 'history' || canAnalyze;
  const [activeTab, setActiveTab] = useState<HubTab>(() => {
    if (requested !== null) return isHubTab(requested) && permitted(requested) ? requested : 'history';
    const remembered = readRememberedTab();
    return remembered && permitted(remembered) ? remembered : 'history';
  });
  const [visited, setVisited] = useState<Record<HubTab, boolean>>({
    analysis: activeTab === 'analysis',
    history: activeTab === 'history',
  });
  const availableTabs: HubTab[] = canAnalyze ? ['analysis', 'history'] : ['history'];

  const selectTab = (tab: HubTab) => {
    if (!permitted(tab)) tab = 'history';
    setActiveTab(tab);
    setVisited(current => ({ ...current, [tab]: true }));
    setSearchParams({ tab }, { replace: true });
    try {
      localStorage.setItem(STORAGE_KEY, tab);
    } catch {
      // Storage can be unavailable in private browsing or restricted environments.
    }
  };

  useEffect(() => {
    if (requested !== null) {
      selectTab(isHubTab(requested) ? requested : 'history');
    } else if (!permitted(activeTab)) {
      selectTab('history');
    }
    // React only needs to respond when the requested URL tab or capability changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, canAnalyze]);

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tab: HubTab) => {
    const index = availableTabs.indexOf(tab);
    let next: HubTab | undefined;
    if (event.key === 'ArrowLeft') next = availableTabs[(index + availableTabs.length - 1) % availableTabs.length];
    if (event.key === 'ArrowRight') next = availableTabs[(index + 1) % availableTabs.length];
    if (event.key === 'Home') next = availableTabs[0];
    if (event.key === 'End') next = availableTabs[availableTabs.length - 1];
    if (!next) return;
    event.preventDefault();
    selectTab(next);
    document.getElementById(`ai-hub-tab-${next}`)?.focus();
  };

  const tabClass = (tab: HubTab) => `min-h-11 border-b-2 px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
    activeTab === tab
      ? 'border-amber-500 text-amber-700 dark:text-amber-300'
      : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
  }`;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">AI Hub</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">Create AI-powered team insights and revisit your conversations.</p>
      </header>
      <div role="tablist" aria-label="AI Hub sections" className="flex border-b border-gray-200 dark:border-gray-700">
        {canAnalyze && (
          <button
            id="ai-hub-tab-analysis"
            type="button"
            role="tab"
            aria-controls="ai-hub-panel-analysis"
            aria-selected={activeTab === 'analysis'}
            tabIndex={activeTab === 'analysis' ? 0 : -1}
            className={tabClass('analysis')}
            onClick={() => selectTab('analysis')}
            onKeyDown={event => handleTabKeyDown(event, 'analysis')}
          >
            Create analysis
          </button>
        )}
        <button
          id="ai-hub-tab-history"
          type="button"
          role="tab"
          aria-controls="ai-hub-panel-history"
          aria-selected={activeTab === 'history'}
          tabIndex={activeTab === 'history' ? 0 : -1}
          className={tabClass('history')}
          onClick={() => selectTab('history')}
          onKeyDown={event => handleTabKeyDown(event, 'history')}
        >
          History
        </button>
      </div>
      {canAnalyze && (
        <section
          id="ai-hub-panel-analysis"
          role="tabpanel"
          aria-labelledby="ai-hub-tab-analysis"
          data-testid="analysis-panel"
          hidden={activeTab !== 'analysis'}
        >
          {visited.analysis && (
            <PanelErrorBoundary label="Create analysis">
              <AnalysisPage embedded />
            </PanelErrorBoundary>
          )}
        </section>
      )}
      <section
        id="ai-hub-panel-history"
        role="tabpanel"
        aria-labelledby="ai-hub-tab-history"
        data-testid="history-panel"
        hidden={activeTab !== 'history'}
      >
        {visited.history && (
          <PanelErrorBoundary label="History">
            <ChatHistoryPage embedded />
          </PanelErrorBoundary>
        )}
      </section>
    </div>
  );
};

export default AIHubPage;
